import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import client from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('ap_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const login = useCallback(async (email, password) => {
    const response = await client.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('ap_token', access_token);
    localStorage.setItem('ap_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await client.post('/auth/logout');
    } catch {
      // ignore logout errors
    } finally {
      localStorage.removeItem('ap_token');
      localStorage.removeItem('ap_user');
      setUser(null);
    }
  }, []);

  // Maintenance gate — poll every 60s; redirect non-app-admin users when ON.
  // Never redirect on /login or /maintenance — app_admin must be able to sign in.
  useEffect(() => {
    const check = () => {
      const path = window.location.pathname;
      if (path === '/maintenance' || path === '/login') return;
      client.get('/maintenance/status').then(r => {
        if (r.data.maintenance && user?.role !== 'app_admin') {
          window.location.href = '/maintenance';
        }
      }).catch(() => {});
    };
    check();
    const t = setInterval(check, 60_000);
    return () => clearInterval(t);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
